/**
 * Centralized password validation utilities
 */

export interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
  rules: {
    lengthGreaterThanEight: boolean;
    atLeastOneUppercaseLetter: boolean;
    atLeastOneLowercaseLetter: boolean;
    atLeastOneNumberOrSymbol: boolean;
  };
}

/**
 * Validates password strength and returns detailed validation result
 */
export function validatePassword(password: string): PasswordValidationResult {
  const errors: string[] = [];
  const rules = {
    atLeastOneLowercaseLetter: /[a-z]/.test(password),
    atLeastOneNumberOrSymbol: /[0-9!@#$%^&*()_\-+=\[\]{}|;:'",./<>?]/.test(
      password
    ),
    atLeastOneUppercaseLetter: /[A-Z]/.test(password),
    lengthGreaterThanEight: password.length >= 8,
  };

  if (!rules.lengthGreaterThanEight) {
    errors.push('Password must be at least 8 characters');
  }

  if (!rules.atLeastOneUppercaseLetter) {
    errors.push('Password must contain at least one uppercase letter');
  }

  if (!rules.atLeastOneLowercaseLetter) {
    errors.push('Password must contain at least one lowercase letter');
  }

  if (!rules.atLeastOneNumberOrSymbol) {
    errors.push(
      'Password must contain at least one number or symbol (!@#$%^&*()_-+=[]{}|;:\'",.<>/?)'
    );
  }

  return {
    errors,
    isValid: errors.length === 0,
    rules,
  };
}

/**
 * Validates password and returns the first error message or null if valid
 */
export function validatePasswordSimple(password: string): string | null {
  const result = validatePassword(password);
  return result.isValid ? null : result.errors[0];
}

/**
 * Validates password confirmation
 */
export function validatePasswordConfirmation(
  password: string,
  confirmPassword: string
): string | null {
  if (password !== confirmPassword) {
    return 'Passwords do not match';
  }
  return null;
}
